import * as React from 'react';
import { Switch, Route, RouteComponentProps, withRouter } from 'react-router';
import ErrorPage from '../../pages/ErrorPage/ErrorPage';
import { default as AdminUsersPage } from '../../pages/Admin/UsersPage/UsersPage';
import { default as AdminUserPage } from '../../pages/Admin/UsersPage/UserPage/UserPage';
import { default as SetupMembersPage } from '../../pages/Setup/MembersPage/MembersPage';
import { default as SetupMembersImportPage } from '../../pages/Setup/MembersPage/ImportPage/ImportPage';
import { default as SetupProjectIssuesPage } from '../../pages/Setup/ProjectIssuesPage/ProjectIssuesPage';
import { default as SetupProjectSubmissionsPage } from '../../pages/Setup/ProjectSubmissionsPage/ProjectSubmissionsPage';
import { default as SetupProjectSubmissionPage } from '../../pages/Setup/ProjectSubmissionsPage/ProjectSubmissionPage/ProjectSubmissionPage';
import { default as SetupProjectMembersPage } from '../../pages/Setup/ProjectMembersPage/ProjectMembersPage';
import { default as SetupProjectMembersImportPage } from '../../pages/Setup/ProjectMembersPage/ImportPage/ImportPage';
import { default as SyncConnectionsPage } from '../../pages/Sync/ConnectionsPage/ConnectionsPage';
import { default as SyncConnectionPage } from '../../pages/Sync/ConnectionsPage/ConnectionPage/ConnectionPage';
import { default as SyncFilesPage } from '../../pages/Sync/FilesPage/FilesPage';
import LoginPage from '../../pages/LoginPage/LoginPage';
import TasksPage from '../../pages/Tasks/TasksPage/TasksPage';
import TaskLinkFilePage from '../../pages/Tasks/TasksPage/TaskSyncPage/TaskLinkFilePage/TaskLinkFilePage';
import TaskSyncPage from '../../pages/Tasks/TasksPage/TaskSyncPage/TaskSyncPage';
import TaskLinkPage from '../../pages/Tasks/TasksPage/TaskSyncPage/TaskLinkPage/TaskLinkPage';
import TaskMemberPage from '../../pages/Tasks/TasksPage/TaskMemberPage/TaskMemberPage';
import ProtectedRoute, {
    ProtectedAdminRoute,
    ProtectedReportRoute,
    ProtectedSetupRoute,
    ProtectedSyncRoute,
} from '../ProtectedRoute/ProtectedRoute';
import HomePage from '../../pages/HomePage/HomePage';
import TasksProjectIssuesPage from '../../pages/Tasks/TasksPage/TasksProjectIssuesPage/TasksProjectIssuesPage';
import TaskProjectIssueActionPage from '../../pages/Tasks/TasksPage/TasksProjectIssuesPage/TaskProjectIssueActionPage/TaskProjectIssueActionPage';
import { ApplicationType } from '../../model/types';
import CustomContext from '../../context/CustomContext';
import SettingsPage from '../../pages/SettingsPage/SettingsPage';
import ReportsPage from '../../pages/Reports/ReportsPage/ReportsPage';
import DesignRatingReportPage from '../../pages/Reports/ReportsPage/DesignRatingReportsPage/DesignRatingReportPage/DesignRatingReportPage';
import DesignRatingReportsPage from '../../pages/Reports/ReportsPage/DesignRatingReportsPage/DesignRatingReportsPage';
import ChecklistDetailsReportsPage from '../../pages/Reports/ReportsPage/ChecklistDetailsReportsPage/ChecklistDetailsReportsPage';
import ChecklistDetailsReportPage from '../../pages/Reports/ReportsPage/ChecklistDetailsReportsPage/ChecklistDetailsReportPage/ChecklistDetailsReportPage';

class RoutesComponent extends React.Component<Props> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;

    componentDidMount() {
        this.loadApplication();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.location && prevProps.location !== this.props.location) {
            this.loadApplication();
        }
    }

    /**
     * Loads the current application.
     */
    loadApplication = () => {
        const application = this.getApplication(this.props.location.pathname);
        if (application !== this.context.application) {
            this.context.updateApplication(application);
        }
    };

    /**
     * Returns the application based on the current path and .
     * @param pathname - the path name
     * @returns  the application
     */
    getApplication = (pathname: string): ApplicationType | undefined => {
        if (pathname.startsWith('/sync')) {
            return 'SYNC';
        } else if (pathname.startsWith('/setup')) {
            return 'SETUP';
        } else if (pathname.startsWith('/reports')) {
            return 'REPORTS';
        } else if (pathname.startsWith('/admin')) {
            return 'ADMIN';
        } else if (pathname.startsWith('/task') || pathname.startsWith('/settings')) {
            return this.context.application;
        }
    };

    render() {
        return (
            <Switch>
                <ProtectedRoute exact path="/" component={HomePage} />
                <ProtectedAdminRoute exact path="/admin" component={AdminUsersPage} />
                <ProtectedAdminRoute exact path="/admin/users" component={AdminUsersPage} />
                <ProtectedAdminRoute exact path="/admin/users/:id" component={AdminUserPage} />

                <ProtectedSetupRoute exact path="/setup" component={SetupProjectMembersPage} />
                <ProtectedSetupRoute exact path="/setup/project-members" component={SetupProjectMembersPage} />
                <ProtectedSetupRoute
                    exact
                    path="/setup/project-members/import/:provider/:hubId"
                    component={SetupProjectMembersImportPage}
                />
                <ProtectedRoute
                    exact
                    path="/setup/project-issues"
                    authorities={['ROLE_SETUP', 'ROLE_ISSUES']}
                    component={SetupProjectIssuesPage}
                    type="all"
                />
                <ProtectedSetupRoute exact path="/setup/project-submissions" component={SetupProjectSubmissionsPage} />
                <ProtectedSetupRoute
                    exact
                    path="/setup/project-submissions/:id"
                    component={SetupProjectSubmissionPage}
                />
                <ProtectedRoute
                    exact
                    path="/setup/members"
                    component={SetupMembersPage}
                    authorities={['ROLE_SETUP', 'ROLE_BIM360_ADMIN']}
                    type="all"
                />
                <ProtectedRoute
                    exact
                    path="/setup/members/import"
                    component={SetupMembersImportPage}
                    authorities={['ROLE_SETUP', 'ROLE_BIM360_ADMIN']}
                    type="all"
                />

                <ProtectedSyncRoute exact path="/sync" component={SyncConnectionsPage} />
                <ProtectedSyncRoute exact path="/sync/connections" component={SyncConnectionsPage} />
                <ProtectedSyncRoute exact path="/sync/connections/:id" component={SyncConnectionPage} />
                <ProtectedSyncRoute exact path="/sync/files" component={SyncFilesPage} />

                <ProtectedReportRoute exact path="/reports" component={ReportsPage} />
                <ProtectedReportRoute exact path="/reports/design-ratings" component={DesignRatingReportsPage} />
                <ProtectedReportRoute exact path="/reports/design-ratings/:id" component={DesignRatingReportPage} />
                <ProtectedReportRoute exact path="/reports/checklist-details" component={ChecklistDetailsReportsPage} />
                <ProtectedReportRoute
                    exact
                    path="/reports/checklist-details/:id"
                    component={ChecklistDetailsReportPage}
                />

                <ProtectedRoute exact path="/tasks" component={TasksPage} />
                <ProtectedRoute exact path="/tasks/syncs/:id" component={TaskSyncPage} />
                <ProtectedRoute exact path="/task-links/:id" component={TaskLinkPage} />
                <ProtectedRoute exact path="/task-link-files/:id" component={TaskLinkFilePage} />
                <ProtectedRoute exact path="/tasks/members/:id" component={TaskMemberPage} />
                <ProtectedRoute exact path="/tasks/project-issues/:id" component={TasksProjectIssuesPage} />
                <ProtectedRoute exact path="/task-project-issue-actions/:id" component={TaskProjectIssueActionPage} />

                <ProtectedRoute exact path="/settings" component={SettingsPage} />

                <Route exact path="/login" component={LoginPage} />
                <Route exact path="/error" component={ErrorPage} />
                <Route component={ErrorPage} />
            </Switch>
        );
    }
}
export default withRouter(RoutesComponent);

interface Props extends RouteComponentProps {}
